import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useSession } from "@/services/auth";

const API_URL = import.meta.env.VITE_API_URL;

// Request types that can be auto-approved
export const AUTO_APPROVE_TYPES = [
  "RECIPIENT_DELIVERY_INSTRUCTIONS",
  "RECIPIENT_CONTACT_UPDATE",
  "SENDER_PICKUP_INSTRUCTIONS",
  "SENDER_CONTACT_UPDATE",
] as const;

// All shipment change request types
export type ShipmentChangeRequestType =
  | "RECIPIENT_ADDRESS_CORRECTION"
  | "RECIPIENT_CONTACT_UPDATE"
  | "RECIPIENT_DELIVERY_INSTRUCTIONS"
  | "DELIVERY_RESCHEDULE"
  | "SENDER_ADDRESS_CORRECTION"
  | "SENDER_CONTACT_UPDATE"
  | "SENDER_PICKUP_INSTRUCTIONS"
  | "PICKUP_RESCHEDULE";

export type ShipmentChangeRequestStatus =
  | "PENDING"
  | "ACKNOWLEDGED"
  | "ACTIONED"
  | "CANNOT_FULFILL"
  | "AUTO_APPROVED";

export interface ShipmentChangeRequest {
  id: string;
  status: ShipmentChangeRequestStatus;
  submittedByActorType?: "CUSTOMER" | "CARRIER" | "ADMIN";
  requestTypes: ShipmentChangeRequestType[];
  rescheduleDate?: string;
  reschedulePreference?: string;
  addressCorrection?: string;
  addressLine2Correction?: string;
  deliveryInstructions?: string;
  contactPhone?: string;
  contactName?: string;
  contactEmail?: string;
  additionalNotes?: string;
  adminResponse?: string;
  appliedChanges?: string[];
  createdAt: string;
  handledAt?: string;
}

export interface CreateShipmentChangeRequestInput {
  requestTypes: ShipmentChangeRequestType[];
  rescheduleDate?: string;
  reschedulePreference?: "morning" | "afternoon" | "evening";
  addressCorrection?: string;
  addressLine2Correction?: string;
  deliveryInstructions?: string;
  contactPhone?: string;
  contactName?: string;
  contactEmail?: string;
  additionalNotes?: string;
}

interface CreateShipmentChangeRequestResponse {
  success: boolean;
  message: string;
  request: {
    id: string;
    status: ShipmentChangeRequestStatus;
    requestTypes: ShipmentChangeRequestType[];
    createdAt: string;
  };
  autoApproved: boolean;
  appliedChanges: string[];
}

interface GetShipmentChangeRequestsResponse {
  requests: ShipmentChangeRequest[];
}

/**
 * Submit a change request for a shipment
 */
export function useCreateShipmentChangeRequest(options?: {
  onSuccess?: (data: CreateShipmentChangeRequestResponse) => void;
  onError?: (error: unknown) => void;
}) {
  const queryClient = useQueryClient();
  const { token, customer } = useSession();

  return useMutation({
    mutationFn: async ({
      shipmentId,
      ...input
    }: CreateShipmentChangeRequestInput & { shipmentId: string }) => {
      const { data } = await axios.post<CreateShipmentChangeRequestResponse>(
        `${API_URL}/api/v2/shipments/${shipmentId}/change-requests`,
        input,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return data;
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: ["customerOrdersV2", customer?.id],
      });
      await queryClient.invalidateQueries({
        queryKey: ["changeRequests"],
      });
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
}

/**
 * Get change requests for a shipment
 */
export function useShipmentChangeRequests(shipmentId: string | undefined) {
  const { token } = useSession();

  return useQuery({
    queryKey: ["changeRequests", shipmentId],
    queryFn: async () => {
      if (!shipmentId) throw new Error("Shipment ID required");

      const { data } = await axios.get<GetShipmentChangeRequestsResponse>(
        `${API_URL}/api/v2/shipments/${shipmentId}/change-requests`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return data.requests;
    },
    enabled: !!shipmentId && !!token,
  });
}

/**
 * Helper to check if request types will be auto-approved
 */
export function willBeAutoApproved(
  types: ShipmentChangeRequestType[]
): boolean {
  return types.every((type) =>
    AUTO_APPROVE_TYPES.includes(type as (typeof AUTO_APPROVE_TYPES)[number])
  );
}

/**
 * Format request type for display
 */
export function formatRequestType(type: ShipmentChangeRequestType): string {
  const labels: Record<ShipmentChangeRequestType, string> = {
    RECIPIENT_ADDRESS_CORRECTION: "Recipient address correction",
    RECIPIENT_CONTACT_UPDATE: "Update recipient contact",
    RECIPIENT_DELIVERY_INSTRUCTIONS: "Delivery instructions",
    DELIVERY_RESCHEDULE: "Reschedule delivery",
    SENDER_ADDRESS_CORRECTION: "Sender address correction",
    SENDER_CONTACT_UPDATE: "Update sender contact",
    SENDER_PICKUP_INSTRUCTIONS: "Pickup instructions",
    PICKUP_RESCHEDULE: "Reschedule pickup",
  };
  return labels[type] || type;
}

/**
 * Format status for display
 */
export function formatRequestStatus(
  status: ShipmentChangeRequestStatus
): string {
  const labels: Record<ShipmentChangeRequestStatus, string> = {
    PENDING: "Pending review",
    ACKNOWLEDGED: "Acknowledged",
    ACTIONED: "Handled",
    CANNOT_FULFILL: "Cannot fulfill",
    AUTO_APPROVED: "Applied",
  };
  return labels[status] || status;
}
